import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQuery, useMutation, useApolloClient, gql } from '@apollo/client';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@artstorefronts/ui';
import { Loader2, ShieldOff, Info, ExternalLink, ArrowUpDown, UserX, Ban, RefreshCw, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

import AdminLayout from '../../components/AdminLayout';
import { blurNsfwImages, handleNsfwContainerClick, postRowLabel } from '@arthelper/shared';
import { ADMIN_UPDATE_PROFILE, RESOLVE_SCAN_MUTATION } from '../../graphql/mutations';
import { CONTENT_REVIEW_REQUESTS_QUERY } from '../../graphql/queries';
import ReviewRequestsTable from '../../components/admin/ReviewRequestsTable';
import { getModerationContentUrl, getModerationFlagReason } from '../../utils/moderation-content';

const MODERATION_QUEUE_QUERY = gql`
  query ModerationQueue(
    $status: String
    $contentType: String
    $scanType: String
    $limit: Int
    $offset: Int
    $sortOrder: SortDirection
    $hideNsfwUsers: Boolean
    $userSearch: String
  ) {
    moderationQueue(
      status: $status
      contentType: $contentType
      scanType: $scanType
      limit: $limit
      offset: $offset
      sortOrder: $sortOrder
      hideNsfwUsers: $hideNsfwUsers
      userSearch: $userSearch
    ) {
      edges {
        node {
          id
          contentType
          contentId
          scanType
          provider
          isFlagged
          result
          resolution
          resolvedByType
          resolvedAt
          resolutionNote
          scannedAt
          createdAt
          community {
            id
            name
            slug
          }
          contentOwner {
            asfId
            firstName
            lastName
            profile {
              id
              displayName
              avatarUrl
              slug
              isDisabled
              isBanned
              isNsfw
            }
          }
          image {
            id
            optimizedUrl
            thumbnailUrl
            originalWidth
            originalHeight
            isNsfw
          }
          communityPost {
            id
            slug
            title
            type
            content
            createdAt
            socialImages {
              id
              optimizedUrl
              thumbnailUrl
              originalWidth
              originalHeight
              isNsfw
            }
            textImages {
              id
              optimizedUrl
              thumbnailUrl
              originalWidth
              originalHeight
              isNsfw
            }
            author {
              asfId
              firstName
              lastName
              profile {
                displayName
                avatarUrl
                slug
              }
            }
          }
          communityComment {
            id
            content
            createdAt
            author {
              asfId
              firstName
              lastName
              profile {
                displayName
                avatarUrl
                slug
              }
            }
            post {
              id
              title
              slug
            }
          }
          imageComment {
            id
            content
            createdAt
          }
          socialImage {
            id
            optimizedUrl
            thumbnailUrl
            originalWidth
            originalHeight
            isNsfw
          }
          flaggedFrameCount
          video {
            id
            status
            url
            thumbnailUrl
            duration
            width
            height
          }
          directMessage {
            id
            content
            status
            sender {
              user {
                profile {
                  displayName
                }
              }
            }
          }
          reportedUser {
            id
            slug
            displayName
            isDisabled
            isBanned
            isNsfw
            avatarUrl
            user {
              firstName
              lastName
            }
          }
          connectionRequestMessage
          recommendationBody
          flaggedBy {
            asfId
            firstName
            lastName
            profile {
              displayName
              avatarUrl
              slug
            }
          }
        }
      }
      pageInfo {
        hasNextPage
      }
    }
  }
`;

const MODERATION_COUNTS_QUERY = gql`
  query ModerationQueueCounts($status: String, $hideNsfwUsers: Boolean) {
    moderationQueueCounts(status: $status, hideNsfwUsers: $hideNsfwUsers) {
      all
      items {
        contentType
        count
      }
    }
  }
`;

interface QueueItem {
  id: string;
  contentType: string;
  contentId: string;
  scanType: string;
  provider: string;
  isFlagged: boolean;
  result?: Record<string, any> | null;
  resolution?: string | null;
  resolvedByType?: string | null;
  resolvedAt?: string | null;
  resolutionNote?: string | null;
  createdAt: string;
  community?: { id: string; name: string; slug: string } | null;
  flaggedBy?: {
    asfId?: string;
    firstName?: string;
    lastName?: string;
    profile?: { displayName?: string; avatarUrl?: string; slug?: string } | null;
  } | null;
  contentOwner?: {
    asfId?: string;
    firstName?: string;
    lastName?: string;
    profile?: {
      id?: string;
      displayName?: string;
      avatarUrl?: string;
      slug?: string;
      isDisabled?: boolean;
      isBanned?: boolean;
      isNsfw?: boolean;
    } | null;
  } | null;
  image?: SocialImage | null;
  communityPost?: {
    id: string;
    slug: string;
    title: string;
    type: string;
    content: string | null;
    createdAt: string;
    socialImages?: SocialImage[] | null;
    textImages?: SocialImage[] | null;
    author?: {
      firstName?: string;
      lastName?: string;
      profile?: { displayName?: string; avatarUrl?: string; slug?: string } | null;
    } | null;
  } | null;
  communityComment?: {
    id: string;
    content: string | null;
    createdAt: string;
    author?: {
      firstName?: string;
      lastName?: string;
      profile?: { displayName?: string; avatarUrl?: string; slug?: string } | null;
    } | null;
    post?: { id: string; title: string; slug: string } | null;
  } | null;
  // Artwork comment. Its author renders from contentOwner, which the scan
  // resolves for image_comment rows, so the selection stays this small.
  imageComment?: {
    id: string;
    content: string | null;
    createdAt: string;
  } | null;
  socialImage?: SocialImage | null;
  video?: FlaggedVideo | null;
  // How many keyframes of a video flagged. The queue shows one row per flagged
  // clip, not one per frame, so the row says how much of the clip it stands for.
  flaggedFrameCount?: number | null;
  directMessage?: {
    id: string;
    content: string | null;
    status: string;
    sender?: { user?: { profile?: { displayName?: string } | null } | null } | null;
  } | null;
  reportedUser?: {
    id: string;
    slug: string;
    displayName?: string | null;
    isDisabled?: boolean;
    isBanned?: boolean;
    isNsfw?: boolean;
    avatarUrl?: string | null;
    user?: { firstName?: string | null; lastName?: string | null } | null;
  } | null;
  connectionRequestMessage?: string | null;
  recommendationBody?: string | null;
}

interface SocialImage {
  id: string;
  optimizedUrl?: string | null;
  thumbnailUrl?: string | null;
  originalWidth?: number | null;
  originalHeight?: number | null;
  isNsfw?: boolean;
}

interface FlaggedVideo {
  id: string;
  status: string;
  url?: string | null;
  thumbnailUrl?: string | null;
  duration?: number | null;
  width?: number | null;
  height?: number | null;
}

/**
 * The still every preview is built around: the flagged image, or for a video the
 * poster frame the transcode extracted. A Video is normalized into the shape the
 * image branches already render rather than growing a parallel preview path — the
 * player the preview adds on top is one attribute, not a second layout.
 */
function flaggedStill(item: QueueItem): SocialImage | null {
  if (item.socialImage) return item.socialImage;
  if (item.image) return item.image;
  if (!item.video?.thumbnailUrl) return null;
  return {
    id: item.video.id,
    thumbnailUrl: item.video.thumbnailUrl,
    originalWidth: item.video.width,
    originalHeight: item.video.height,
  };
}

/**
 * The playable rendition behind that still, when there is one. A single flagged
 * keyframe is what the scan caught, not what the decision is about — a clip is
 * judged by watching it — so the preview plays the video the poster came from.
 *
 * Null when the still came from an image rather than a video, and null while the
 * clip has no rendition to serve: `processing` and `failed` rows have nothing in
 * the bucket yet, so the poster is all a moderator gets.
 */
function flaggedVideoUrl(item: QueueItem): string | null {
  if (item.socialImage || item.image) return null;
  return item.video?.url || null;
}

/**
 * A flagged clip behind the same blur the stills sit behind, plus the one thing a
 * filter cannot do: hiding a revealed clip has to stop it. `blur` is a CSS filter
 * and `controls` is chrome — neither pauses playback, so a moderator who reveals a
 * clip, plays it, then hides it again is still listening to it under the overlay.
 * Playback position is kept, since hiding is about the room rather than about the
 * review.
 */
function FlaggedVideo({ src, poster, isBlurred }: { src: string; poster: string; isBlurred: boolean }) {
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (!isBlurred) return;
    ref.current?.pause();
  }, [isBlurred]);

  return (
    <video
      ref={ref}
      src={src}
      poster={poster}
      // Controls appear only once a sensitive clip is revealed, so the reveal stays
      // the one deliberate step before it plays.
      controls={!isBlurred}
      preload="metadata"
      className={`w-full transition-[filter] duration-300 ${isBlurred ? 'blur-[20px]' : ''}`}
    />
  );
}

type ContentTypeFilter =
  | 'all'
  | 'community_post'
  | 'community_comment'
  | 'image_comment'
  | 'image'
  | 'video'
  | 'user'
  | 'review_requests';

function contentTypeLabel(t: TFunction<'admin'>, contentType: string): string {
  if (contentType === 'community_post') return t('moderation.content-type.post');
  if (contentType === 'community_comment') return t('moderation.content-type.comment');
  if (contentType === 'image_comment') return t('moderation.content-type.artwork-comment');
  if (contentType === 'image') return t('moderation.content-type.image');
  if (contentType === 'video') return t('moderation.content-type.video');
  if (contentType === 'direct_message') return t('moderation.content-type.direct-message');
  if (contentType === 'user') return t('moderation.content-type.user');
  if (contentType === 'connection_request') return t('moderation.content-type.connection-request');
  // social_image rows are grouped under their parent type tab and display the parent
  // label via the communityComment/communityPost fallback in the table row renderer.
  if (contentType === 'social_image') return t('moderation.content-type.social-image');
  return contentType;
}

type QueueItemAuthor =
  | NonNullable<NonNullable<QueueItem['communityPost']>['author']>
  | NonNullable<NonNullable<NonNullable<NonNullable<QueueItem['directMessage']>['sender']>['user']>['profile']>
  | NonNullable<QueueItem['contentOwner']>
  | NonNullable<QueueItem['reportedUser']>;

function isDirectMessageItem(item: QueueItem): boolean {
  return item.contentType === 'direct_message' || item.scanType === 'dm_spam';
}

function getAuthor(item: QueueItem): QueueItemAuthor | null {
  if (isDirectMessageItem(item) && item.directMessage?.sender?.user?.profile) {
    return item.directMessage.sender.user.profile;
  }
  if ((item.contentType === 'user' || item.contentType === 'connection_request') && item.reportedUser) {
    return item.reportedUser;
  }
  return item.contentOwner ?? item.communityPost?.author ?? item.communityComment?.author ?? null;
}

function authorDisplayName(
  author: {
    profile?: { displayName?: string } | null;
    firstName?: string | null;
    lastName?: string | null;
    displayName?: string | null;
    slug?: string | null;
    user?: { firstName?: string | null; lastName?: string | null } | null;
  },
  t: TFunction<'admin'>
) {
  const displayName = author.displayName || author.profile?.displayName;
  if (displayName) return displayName;
  const firstName = author.firstName || author.user?.firstName;
  const lastName = author.lastName || author.user?.lastName;
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  if (fullName) return fullName;
  if (author.slug) return `@${author.slug}`;
  return t('moderation.author-unknown');
}

function safeFormatDistance(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return formatDistanceToNow(d, { addSuffix: true })
    .replace(/^(about |over |almost )/, '')
    .replace('less than a minute', '<1m')
    .replace(/(\d+) minutes?/, '$1m')
    .replace(/(\d+) hours?/, '$1h')
    .replace(/(\d+) days?/, '$1d')
    .replace(/(\d+) months?/, '$1mo')
    .replace(/(\d+) years?/, '$1y')
    .replace('a minute', '1m')
    .replace('an hour', '1h')
    .replace('a day', '1d')
    .replace('a month', '1mo')
    .replace('a year', '1y');
}

const STATUS_TAB_VALUES = ['pending', 'approved', 'removed'] as const;
const PAGE_LIMIT = 50;

const NSFW_SCAN_TYPES = new Set(['nsfw']);

/**
 * How long the page waits between `ModerationQueueCounts` fetches. It mirrors the 30s TTL on
 * the server's platform-wide counts cache (`contentScanService.countsForViewer`): asking more
 * often than that can only hand back the same cached numbers.
 */
const COUNTS_REFETCH_WINDOW_MS = 30_000;

function CountBadge({ count, variant = 'default' }: { count: number; variant?: 'default' | 'red' }) {
  if (count === 0) return null;
  return (
    <span
      className={`ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-medium rounded-full ${
        variant === 'red' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
      }`}
    >
      {count}
    </span>
  );
}

function useContentTypeCounts(status: string, hideNsfwUsers: boolean) {
  const { data, refetch } = useQuery(MODERATION_COUNTS_QUERY, {
    variables: { status, hideNsfwUsers },
    fetchPolicy: 'cache-and-network',
  });

  const counts: Record<string, number> = {};
  for (const item of data?.moderationQueueCounts?.items ?? []) {
    counts[item.contentType] = item.count;
  }

  return {
    counts: {
      all: data?.moderationQueueCounts?.all ?? 0,
      community_post: counts['community_post'] ?? 0,
      community_comment: counts['community_comment'] ?? 0,
      image_comment: counts['image_comment'] ?? 0,
      image: counts['image'] ?? 0,
      video: counts['video'] ?? 0,
      user: counts['user'] ?? 0,
    },
    refetchCounts: refetch,
  };
}

/**
 * Keeps the tab badges honest without paying a counts query per decision.
 *
 * The local decrement below can only move the pending tab, and nothing local ever *raises* a
 * badge for content flagged while the moderator works — so every resolve schedules a counts
 * refetch instead, throttled to the server's cache window. A moderator clicking through fifty
 * decisions pays one counts query per window, the badges converge on the truth on every status
 * tab, and the trailing fetch still lands after the last decision.
 *
 * `markCountsFetched` folds the fetches this page does not own — the profile-flag mutations
 * refetch `ModerationQueueCounts` immediately — into the same window, so they open a fresh one
 * and make any queued refetch redundant.
 */
function useThrottledCountsRefetch(refetchCounts: () => Promise<unknown>) {
  // The mount fetch of a `cache-and-network` query opens the first window.
  const lastFetchAtRef = useRef(Date.now());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPending = useCallback(() => {
    if (!timerRef.current) return;
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  // Nothing may fire after the moderator navigates away.
  useEffect(() => clearPending, [clearPending]);

  const runRefetch = useCallback(() => {
    lastFetchAtRef.current = Date.now();
    // Badges are advisory; a failed correction must not surface as an unhandled rejection.
    refetchCounts().catch(() => {});
  }, [refetchCounts]);

  const markCountsFetched = useCallback(() => {
    clearPending();
    lastFetchAtRef.current = Date.now();
  }, [clearPending]);

  const scheduleCountsRefetch = useCallback(() => {
    if (timerRef.current) return;
    const wait = COUNTS_REFETCH_WINDOW_MS - (Date.now() - lastFetchAtRef.current);
    if (wait <= 0) {
      runRefetch();
      return;
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      runRefetch();
    }, wait);
  }, [runRefetch]);

  return { scheduleCountsRefetch, markCountsFetched };
}

interface CountsEntry {
  contentType: string;
  count: number;
}

/**
 * The counts tab a resolved scan leaves, mirroring the rollup in
 * `contentScanService.queueCounts`: a social_image counts under the post or comment it
 * hangs off, and a connection_request counts under `user`. Null when the parent context
 * isn't on the node — the server drops those from every tab and from `all`, which is a
 * sum of the tabs, so a null tab moves no badge at all.
 */
function countsTab(item: QueueItem): string | null {
  if (item.contentType === 'social_image') {
    if (item.communityComment) return 'community_comment';
    if (item.communityPost) return 'community_post';
    return null;
  }
  if (item.contentType === 'connection_request') return 'user';
  return item.contentType;
}

function getDescriptionKey(item: QueueItem): string {
  if (item.contentType === 'community_post') return 'community_post';
  if (item.contentType === 'community_comment') return 'community_comment';
  if (item.contentType === 'image_comment') return 'image_comment';
  if (item.contentType === 'social_image') return item.communityComment ? 'community_comment' : 'community_post';
  if (item.contentType === 'image') return 'image';
  if (item.contentType === 'video') return 'video';
  if (item.contentType === 'direct_message') return 'direct_message';
  if (item.contentType === 'connection_request') return 'connection_request';
  if (item.contentType === 'user') {
    if (item.scanType === 'dm_spam') return 'user-dm_spam';
    if (item.scanType === 'connection_request_spam') return 'user-connection_request_spam';
    if (item.scanType === 'recommendation_spam') return 'user-recommendation_spam';
    if (item.scanType === 'user_report') return 'user-user_report';
  }
  return item.contentType;
}

function isConnectionRequestMessageItem(item: QueueItem): boolean {
  return item.scanType === 'connection_request_spam' || item.contentType === 'connection_request';
}

function isRecommendationMessageItem(item: QueueItem): boolean {
  return item.scanType === 'recommendation_spam';
}

function isUserMessagePreviewItem(item: QueueItem): boolean {
  return isConnectionRequestMessageItem(item) || isRecommendationMessageItem(item);
}

function hasModerationContentPreview(item: QueueItem): boolean {
  return !!(
    item.communityPost ||
    item.communityComment ||
    item.imageComment ||
    !!flaggedStill(item) ||
    item.directMessage ||
    isConnectionRequestMessageItem(item) ||
    isRecommendationMessageItem(item)
  );
}

function moderationPreviewButtonLabel(item: QueueItem, t: TFunction<'admin'>): string {
  if (isUserMessagePreviewItem(item)) {
    return t('moderation.view-message');
  }
  if (isDirectMessageItem(item)) {
    return t('moderation.view-content', {
      contentType: t('moderation.content-type.direct-message'),
    });
  }
  return t('moderation.view-content', {
    contentType: item.communityComment
      ? t('moderation.content-type.comment')
      : item.communityPost
        ? t('moderation.content-type.post')
        : contentTypeLabel(t, item.contentType),
  });
}

function useReviewRequestsCount() {
  const { data } = useQuery(CONTENT_REVIEW_REQUESTS_QUERY, {
    variables: { limit: 1, offset: 0 },
    fetchPolicy: 'cache-and-network',
  });
  return data?.contentReviewRequests?.totalCount ?? 0;
}

export default function AdminModerationPage() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const client = useApolloClient();
  const [status, setStatus] = useState('pending');
  const [contentType, setContentType] = useState<ContentTypeFilter>('all');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [scanTypeFilter, setScanTypeFilter] = useState<string>('all');
  const [userSearch, setUserSearch] = useState('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  const hideNsfwUsers = true;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewItem, setPreviewItem] = useState<QueueItem | null>(null);
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [revealedImages, setRevealedImages] = useState<Set<string>>(new Set());
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string;
    description: string;
    confirm: string;
    confirmVariant?: 'default' | 'destructive';
    onConfirm: () => void;
  } | null>(null);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  // Ref-based guard so the IntersectionObserver can't enqueue a second fetchMore
  // before the first React render propagates the isFetchingMore state update.
  const isFetchingMoreRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const queueLengthRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedUserSearch(userSearch), 400);
    return () => clearTimeout(timer);
  }, [userSearch]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [status, contentType, sortOrder, scanTypeFilter, debouncedUserSearch]);

  const isReviewRequestsTab = contentType === 'review_requests';
  const queryContentType = contentType === 'all' || isReviewRequestsTab ? undefined : contentType;
  const queryScanType = scanTypeFilter === 'all' ? undefined : scanTypeFilter;
  const queryUserSearch = debouncedUserSearch.trim() || undefined;

  const { data, loading, fetchMore, refetch } = useQuery(MODERATION_QUEUE_QUERY, {
    variables: {
      status,
      contentType: queryContentType,
      scanType: queryScanType,
      limit: PAGE_LIMIT,
      offset: 0,
      sortOrder,
      hideNsfwUsers,
      userSearch: queryUserSearch,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: isReviewRequestsTab,
  });

  // Fetch per-type counts for sub-tab badges
  const { counts, refetchCounts } = useContentTypeCounts(status, hideNsfwUsers);
  const { scheduleCountsRefetch, markCountsFetched } = useThrottledCountsRefetch(refetchCounts);
  const reviewRequestsCount = useReviewRequestsCount();

  const isBulkOperationRef = useRef(false);

  const [resolveScan, { loading: reviewLoading }] = useMutation(RESOLVE_SCAN_MUTATION, {
    onCompleted: (data) => {
      setReviewingId(null);
      const resolvedId = data?.resolveScan?.id;
      if (resolvedId) {
        setSelectedIds((prev) => {
          if (!prev.has(resolvedId)) return prev;
          const next = new Set(prev);
          next.delete(resolvedId);
          return next;
        });
      }
      if (!isBulkOperationRef.current) {
        refetch();
        toast.success(t('moderation.toast-review-success'));
      }
    },
    onError: (err) => {
      if (!isBulkOperationRef.current) toast.error(err.message);
    },
  });

  const [adminUpdateProfile, { loading: updatingProfile }] = useMutation(ADMIN_UPDATE_PROFILE, {
    // Flagging a user moves the exclusion set by an amount the client cannot compute, so this
    // one refetches immediately rather than riding the throttle — and opens a fresh window.
    refetchQueries: ['ModerationQueueCounts'],
    onCompleted: markCountsFetched,
    onError: (err) => toast.error(err.message),
  });

  const queue: QueueItem[] = data?.moderationQueue?.edges?.map((e: any) => e.node) ?? [];
  const hasNextPage: boolean = data?.moderationQueue?.pageInfo?.hasNextPage ?? false;

  // Keep the ref in sync so loadMore always reads the current length,
  // even if the Apollo cache hasn't propagated yet during rapid scroll.
  queueLengthRef.current = queue.length;

  // Infinite scroll via IntersectionObserver
  const loadMore = useCallback(async () => {
    if (isFetchingMoreRef.current || !hasNextPage) return;
    isFetchingMoreRef.current = true;
    setIsFetchingMore(true);
    try {
      await fetchMore({
        variables: {
          status,
          contentType: queryContentType,
          scanType: queryScanType,
          limit: PAGE_LIMIT,
          offset: queueLengthRef.current,
          sortOrder,
          hideNsfwUsers,
          userSearch: queryUserSearch,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.moderationQueue?.edges?.length) return prev;
          return {
            moderationQueue: {
              ...fetchMoreResult.moderationQueue,
              edges: [...(prev.moderationQueue?.edges ?? []), ...fetchMoreResult.moderationQueue.edges],
            },
          };
        },
      });
    } finally {
      isFetchingMoreRef.current = false;
      setIsFetchingMore(false);
    }
  }, [hasNextPage, fetchMore, status, queryContentType, queryScanType, sortOrder, hideNsfwUsers, queryUserSearch]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  /**
   * Moves the tab badges the way the resolve just moved the queue, instead of asking the
   * server for counts after every decision. Pending is the only tab a resolve changes
   * deterministically — the row leaves it — so any other status leaves the cache alone and
   * waits for the throttled refetch.
   */
  function applyCountsDecrement(resolved: QueueItem[]) {
    if (status !== 'pending' || resolved.length === 0) return;

    const byTab = resolved.reduce<Record<string, number>>((memo, item) => {
      const tab = countsTab(item);
      if (!tab) return memo;
      memo[tab] = (memo[tab] ?? 0) + 1;
      return memo;
    }, {});

    client.cache.updateQuery({ query: MODERATION_COUNTS_QUERY, variables: { status, hideNsfwUsers } }, (prev: any) => {
      // Not cached yet — the first read is already fetching the fresher number.
      if (!prev?.moderationQueueCounts) return prev;
      const current = prev.moderationQueueCounts;

      // `all` is the sum of the tabs server-side, so it may only move by what the tabs
      // actually move. A tab the server omitted — its count was 0 when these numbers were
      // read — has no entry to decrement, so a row of that type moves nothing at all rather
      // than dropping `all` on its own; the throttled refetch is what corrects it.
      const cached: CountsEntry[] = current.items ?? [];
      const { items, dropped } = cached.reduce<{ items: CountsEntry[]; dropped: number }>(
        (memo, entry) => {
          const drop = Math.min(entry.count, byTab[entry.contentType] ?? 0);
          memo.items.push(drop ? { ...entry, count: entry.count - drop } : entry);
          memo.dropped += drop;
          return memo;
        },
        { items: [], dropped: 0 }
      );
      if (dropped === 0) return prev;

      return {
        ...prev,
        moderationQueueCounts: { ...current, all: Math.max(0, (current.all ?? 0) - dropped), items },
      };
    });
  }

  function handleStatusChange(newStatus: string) {
    setStatus(newStatus);
    setContentType('all');
    setScanTypeFilter('all');
    setSelectedIds(new Set());
  }

  async function handleReview(scanId: string, resolution: string, note?: string) {
    setReviewingId(scanId);
    const item = queue.find((queued) => queued.id === scanId);
    const result = await resolveScan({ variables: { input: { scanId, resolution, ...(note && { note }) } } });
    if (!result?.data?.resolveScan) return;
    applyCountsDecrement(item ? [item] : []);
    scheduleCountsRefetch();
  }

  function handleDisableUser(profileId: string, displayName: string | null | undefined) {
    setPendingConfirm({
      title: t('moderation.confirm-disable-user.title', {
        name: displayName || t('moderation.author-unknown'),
      }),
      description: t('moderation.confirm-disable-user.description', {
        name: displayName || t('moderation.author-unknown'),
      }),
      confirm: t('moderation.action-disable-user'),
      onConfirm: async () => {
        await adminUpdateProfile({ variables: { input: { profileId, isDisabled: true } } });
        toast.success(t('moderation.toast-user-disabled'));
        refetch();
      },
    });
  }

  function handleNsfwUser(profileId: string, displayName: string | null | undefined) {
    setPendingConfirm({
      title: t('moderation.confirm-nsfw-user.title', {
        name: displayName || t('moderation.author-unknown'),
      }),
      description: t('moderation.confirm-nsfw-user.description', {
        name: displayName || t('moderation.author-unknown'),
      }),
      confirm: t('moderation.action-nsfw-user'),
      onConfirm: async () => {
        await adminUpdateProfile({ variables: { input: { profileId, isNsfw: true } } });
        toast.success(t('moderation.toast-user-nsfw'));
        refetch();
      },
    });
  }

  function handleBanContentOwner(profileId: string, displayName: string | null | undefined) {
    setPendingConfirm({
      title: t('moderation.confirm-ban-user.title', {
        name: displayName || t('moderation.author-unknown'),
      }),
      description: t('moderation.confirm-ban-user.description', {
        name: displayName || t('moderation.author-unknown'),
      }),
      confirm: t('moderation.action-ban-user'),
      confirmVariant: 'destructive',
      onConfirm: async () => {
        await adminUpdateProfile({ variables: { input: { profileId, isBanned: true } } });
        toast.success(t('moderation.toast-user-banned'));
        refetch();
      },
    });
  }

  function confirmBulkAction({
    confirmKey,
    resolution,
    note,
  }: {
    confirmKey: 'confirm-bulk-allow' | 'confirm-bulk-remove';
    resolution: string;
    note?: string;
  }) {
    const count = selectedIds.size;
    setPendingConfirm({
      title: t(`moderation.${confirmKey}.title`, { count }),
      description: t(`moderation.${confirmKey}.description`, { count }),
      confirm: t(`moderation.${confirmKey}.confirm`, { count }),
      onConfirm: async () => {
        isBulkOperationRef.current = true;
        const ids = [...selectedIds];
        let resolvedIds: string[] = [];
        let successCount = 0;
        let failCount = 0;
        try {
          const results = await Promise.allSettled(
            ids.map((id) => resolveScan({ variables: { input: { scanId: id, resolution, ...(note && { note }) } } }))
          );
          resolvedIds = ids.filter((_, index) => {
            const result = results[index];
            return result.status === 'fulfilled' && result.value?.data != null;
          });
          successCount = resolvedIds.length;
          failCount = results.length - successCount;
        } finally {
          isBulkOperationRef.current = false;
          setSelectedIds(new Set());
          // Only the ids that came back resolved — a partial failure must not move a badge
          // past what the server actually cleared.
          applyCountsDecrement(
            resolvedIds
              .map((id) => queue.find((queued) => queued.id === id))
              .filter((item): item is QueueItem => !!item)
          );
          await refetch();
          if (successCount > 0) {
            scheduleCountsRefetch();
            toast.success(t('moderation.toast-review-success'));
          }
          if (failCount > 0) {
            toast.error(t('moderation.toast-bulk-error', { count: failCount }));
          }
        }
      },
    });
  }

  function handleBulkApprove() {
    confirmBulkAction({ confirmKey: 'confirm-bulk-allow', resolution: 'approved' });
  }

  function handleBulkRemove() {
    confirmBulkAction({
      confirmKey: 'confirm-bulk-remove',
      resolution: 'rejected',
      note: t('moderation.violates-community-rules'),
    });
  }

  function toggleImageReveal(imageId: string) {
    setRevealedImages((prev) => {
      const next = new Set(prev);
      if (next.has(imageId)) next.delete(imageId);
      else next.add(imageId);
      return next;
    });
  }

  const pendingCount = status === 'pending' ? counts.all : 0;

  return (
    <AdminLayout title={t('moderation.page-title')} description={t('moderation.page-description')}>
      {/* User search */}
      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <Input
          type="text"
          placeholder={t('moderation.user-search-placeholder')}
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          className="pl-8 pr-8"
        />
        {userSearch && (
          <button
            onClick={() => setUserSearch('')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            aria-label={t('moderation.clear-search')}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Status tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {STATUS_TAB_VALUES.map((tabValue) => (
          <button
            key={tabValue}
            onClick={() => handleStatusChange(tabValue)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              status === tabValue ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t(`moderation.status.${tabValue}`)}
            {tabValue === 'pending' && !queryUserSearch && pendingCount > 0 && (
              <CountBadge count={pendingCount} variant="red" />
            )}
          </button>
        ))}
      </div>

      {/* Content-type sub-tabs + sort */}
      <div className="flex items-center justify-between mb-4">
        <Tabs
          value={contentType}
          onValueChange={(val) => {
            setContentType(val as ContentTypeFilter);
          }}
        >
          <TabsList>
            <TabsTrigger value="all">
              {t('moderation.content-type.all')}
              {!queryUserSearch && <CountBadge count={counts.all} />}
            </TabsTrigger>
            <TabsTrigger value="community_post">
              {t('moderation.content-type.post')}
              {!queryUserSearch && <CountBadge count={counts.community_post} />}
            </TabsTrigger>
            <TabsTrigger value="community_comment">
              {t('moderation.content-type.comment')}
              {!queryUserSearch && <CountBadge count={counts.community_comment} />}
            </TabsTrigger>
            <TabsTrigger value="image_comment">
              {t('moderation.content-type.artwork-comment')}
              {!queryUserSearch && <CountBadge count={counts.image_comment} />}
            </TabsTrigger>
            <TabsTrigger value="image">
              {t('moderation.content-type.image')}
              {!queryUserSearch && <CountBadge count={counts.image} />}
            </TabsTrigger>
            <TabsTrigger value="video">
              {t('moderation.content-type.video')}
              {!queryUserSearch && <CountBadge count={counts.video} />}
            </TabsTrigger>
            <TabsTrigger value="user">
              {t('moderation.content-type.user')}
              {!queryUserSearch && <CountBadge count={counts.user} />}
            </TabsTrigger>
            <TabsTrigger value="review_requests">
              {t('moderation.content-type.review-requests')}
              <CountBadge count={reviewRequestsCount} variant="red" />
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {contentType !== 'review_requests' && (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="flex items-center gap-1.5 text-xs"
              onClick={() => setSortOrder((prev) => (prev === 'DESC' ? 'ASC' : 'DESC'))}
            >
              <ArrowUpDown className="w-3.5 h-3.5" />
              {sortOrder === 'DESC' ? t('moderation.sort.newest-first') : t('moderation.sort.oldest-first')}
            </Button>
            <Select value={scanTypeFilter} onValueChange={setScanTypeFilter}>
              <SelectTrigger className="h-7 text-xs w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('moderation.scan-type.all')}</SelectItem>
                <SelectItem value="nsfw">{t('moderation.scan-type.nsfw')}</SelectItem>
                <SelectItem value="ai_detection">{t('moderation.scan-type.ai-detection')}</SelectItem>
                <SelectItem value="user_report">{t('moderation.scan-type.user-report')}</SelectItem>
                <SelectItem value="moderator_action">{t('moderation.scan-type.moderator-action')}</SelectItem>
                <SelectItem value="comment_spam">{t('moderation.scan-type.comment-spam')}</SelectItem>
                <SelectItem value="dm_spam">{t('moderation.scan-type.dm-spam')}</SelectItem>
                <SelectItem value="connection_request_spam">
                  {t('moderation.scan-type.connection-request-spam')}
                </SelectItem>
                <SelectItem value="recommendation_spam">{t('moderation.scan-type.recommendation-spam')}</SelectItem>
              </SelectContent>
            </Select>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 w-7 p-0"
                    onClick={() => refetch()}
                    disabled={loading}
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">{t('moderation.refresh')}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </div>

      {contentType === 'review_requests' ? (
        <ReviewRequestsTable />
      ) : loading && queue.length === 0 ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : queue.length === 0 ? (
        <div className="text-center py-16 text-gray-500 border border-dashed border-gray-200 rounded-lg">
          {t('moderation.empty-queue', {
            status: t(`moderation.status.${status as (typeof STATUS_TAB_VALUES)[number]}`),
          })}
        </div>
      ) : (
        <>
          {selectedIds.size > 0 && (
            <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-2 mb-3 bg-gray-50 border border-gray-200 rounded-lg">
              <span className="text-sm text-gray-600 mr-1">
                {t('moderation.bulk-bar.selected', { count: selectedIds.size })}
              </span>
              <>
                <Button
                  size="sm"
                  className="bg-green-500 hover:bg-green-600 text-white text-xs h-7 px-3"
                  onClick={handleBulkApprove}
                >
                  {t('moderation.bulk-bar.allow', { count: selectedIds.size })}
                </Button>
                <Button size="sm" variant="destructive" className="text-xs h-7 px-3" onClick={handleBulkRemove}>
                  {t('moderation.bulk-bar.remove', { count: selectedIds.size })}
                </Button>
              </>

              <button
                onClick={() => setSelectedIds(new Set())}
                className="ml-auto text-xs text-gray-400 hover:text-gray-600"
              >
                {t('moderation.bulk-bar.clear-selection')}
              </button>
            </div>
          )}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full table-fixed text-left">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  {status === 'pending' && (
                    <th className="pl-4 py-3 w-8">
                      <Checkbox
                        checked={queue.length > 0 && queue.every((item) => selectedIds.has(item.id))}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedIds(new Set(queue.map((item) => item.id)));
                          } else {
                            setSelectedIds(new Set());
                          }
                        }}
                        aria-label={t('moderation.bulk-bar.select-all')}
                      />
                    </th>
                  )}
                  <th className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    {t('moderation.table.type')}
                  </th>
                  <th className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    {t('moderation.table.community')}
                  </th>
                  <th className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider w-44">
                    {t('moderation.table.user')}
                  </th>
                  <th className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                    {t('moderation.table.content')}
                  </th>
                  <th className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('moderation.table.reason')}
                  </th>
                  <th className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                    {t('moderation.table.reported')}
                  </th>
                  <th className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider w-80 text-right">
                    {t('moderation.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {queue.map((item) => {
                  const author = getAuthor(item);
                  const displayName = author ? authorDisplayName(author, t) : null;
                  const isUserReport = item.contentType === 'user' || item.contentType === 'connection_request';
                  // For user/connection_request rows, the author IS the reportedUser profile.
                  // For other types, the author is a user object with a nested .profile.
                  const authorProfile = isUserReport
                    ? (item.reportedUser ?? null)
                    : author && 'profile' in author
                      ? (author.profile as { avatarUrl?: string | null; slug?: string | null } | null)
                      : null;
                  const contentUrl = getModerationContentUrl(item);
                  const ownerProfile = item.contentOwner?.profile;

                  return (
                    <tr key={item.id} className={`hover:bg-gray-50 ${selectedIds.has(item.id) ? 'bg-blue-50' : ''}`}>
                      {status === 'pending' && (
                        <td className="pl-4 py-3">
                          <Checkbox
                            checked={selectedIds.has(item.id)}
                            onCheckedChange={(checked) => {
                              setSelectedIds((prev) => {
                                const next = new Set(prev);
                                if (checked) next.add(item.id);
                                else next.delete(item.id);
                                return next;
                              });
                            }}
                            aria-label={t('moderation.bulk-bar.select-item')}
                          />
                        </td>
                      )}
                      <td className="px-3 py-2.5">
                        <span className="text-sm font-medium text-gray-900">
                          {item.communityComment
                            ? t('moderation.content-type.comment')
                            : item.communityPost
                              ? t('moderation.content-type.post')
                              : contentTypeLabel(t, item.contentType)}
                        </span>
                        {/* One row stands for every flagged keyframe of the clip, so it says how many. */}
                        {(item.flaggedFrameCount ?? 0) > 1 && (
                          <span className="block text-[10px] text-gray-500">
                            {t('moderation.flagged-frames', { count: item.flaggedFrameCount })}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        {item.community ? (
                          <a
                            href={`${__TOWN_SQUARE_URL__}/community/${item.community.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-gray-900 hover:underline truncate block max-w-[160px]"
                          >
                            {item.community.name}
                          </a>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 overflow-hidden">
                        {author ? (
                          <div className="flex items-center gap-2">
                            {authorProfile?.slug ? (
                              <a
                                href={`${__TOWN_SQUARE_URL__}/${authorProfile.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="shrink-0"
                              >
                                {authorProfile?.avatarUrl ? (
                                  <img
                                    src={authorProfile.avatarUrl}
                                    alt=""
                                    className="w-7 h-7 rounded-full object-cover hover:opacity-80 transition-opacity"
                                  />
                                ) : (
                                  <div className="w-7 h-7 rounded-full bg-gray-200 hover:opacity-80 transition-opacity" />
                                )}
                              </a>
                            ) : authorProfile?.avatarUrl ? (
                              <img
                                src={authorProfile.avatarUrl}
                                alt=""
                                className="w-7 h-7 rounded-full object-cover shrink-0"
                              />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-gray-200 shrink-0" />
                            )}
                            <div className="flex flex-col min-w-0">
                              {authorProfile?.slug ? (
                                <a
                                  href={`${__TOWN_SQUARE_URL__}/${authorProfile.slug}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-medium text-gray-900 hover:underline truncate"
                                >
                                  {displayName}
                                </a>
                              ) : (
                                <span className="text-sm font-medium text-gray-900 truncate">{displayName}</span>
                              )}
                              <div className="flex gap-1 mt-0.5">
                                {(ownerProfile?.isDisabled || item.reportedUser?.isDisabled) && (
                                  <span className="text-[10px] bg-red-100 text-red-600 px-1 rounded">
                                    {t('moderation.user-flag.disabled')}
                                  </span>
                                )}
                                {(ownerProfile?.isBanned || item.reportedUser?.isBanned) && (
                                  <span className="text-[10px] bg-red-200 text-red-800 px-1 rounded">
                                    {t('moderation.user-flag.banned')}
                                  </span>
                                )}
                                {(ownerProfile?.isNsfw || item.reportedUser?.isNsfw) && (
                                  <span className="text-[10px] bg-orange-100 text-orange-600 px-1 rounded">
                                    {t('moderation.user-flag.nsfw')}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5">
                          {hasModerationContentPreview(item) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7 px-2"
                              onClick={() => setPreviewItem(item)}
                            >
                              {moderationPreviewButtonLabel(item, t)}
                            </Button>
                          )}
                          {(contentUrl || (item.contentType === 'user' && item.reportedUser)) && (
                            <a
                              href={contentUrl || `${__TOWN_SQUARE_URL__}/${item.reportedUser?.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-400 hover:text-gray-700 shrink-0"
                              title={t('moderation.open-in-new-tab')}
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm text-gray-900 capitalize">
                            {(getModerationFlagReason(item) || '—').replace(/_/g, ' ')}
                          </span>
                          {item.result &&
                            typeof item.result === 'object' &&
                            'details' in item.result &&
                            item.result.details && (
                              <HoverCard>
                                <HoverCardTrigger asChild>
                                  <button className="text-gray-400 hover:text-gray-600">
                                    <Info className="w-3.5 h-3.5" />
                                  </button>
                                </HoverCardTrigger>
                                <HoverCardContent className="w-72 text-sm text-gray-700">
                                  {String(item.result.details)}
                                </HoverCardContent>
                              </HoverCard>
                            )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 overflow-hidden">
                        <span className="text-sm text-gray-500">{safeFormatDistance(item.createdAt)}</span>
                        {item.flaggedBy ? (
                          <div className="flex items-center gap-1.5 mt-0.5 overflow-hidden">
                            {item.flaggedBy.profile?.avatarUrl ? (
                              <img
                                src={item.flaggedBy.profile.avatarUrl}
                                alt=""
                                className="w-4 h-4 rounded-full object-cover shrink-0"
                              />
                            ) : (
                              <div className="w-4 h-4 rounded-full bg-gray-200 shrink-0" />
                            )}
                            {item.flaggedBy.profile?.slug ? (
                              <a
                                href={`${__TOWN_SQUARE_URL__}/${item.flaggedBy.profile.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-gray-600 hover:underline truncate min-w-0"
                              >
                                {authorDisplayName(item.flaggedBy as any, t)}
                              </a>
                            ) : (
                              <span className="text-xs text-gray-600 truncate min-w-0">
                                {authorDisplayName(item.flaggedBy as any, t)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[11px] text-red-500 block mt-0.5">{t('moderation.auto-flagged')}</span>
                        )}
                        {item.resolution === 'deleted' && item.resolvedByType === 'user' && (
                          <span className="text-[11px] text-gray-400 block mt-0.5 whitespace-nowrap">
                            {t('moderation.deleted-by-user')}
                          </span>
                        )}
                        {item.resolution === 'deleted' && item.resolvedByType === 'admin' && (
                          <span className="text-[11px] text-gray-400 block mt-0.5 whitespace-nowrap">
                            {t('moderation.deleted-by-admin')}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex justify-end gap-1.5 flex-nowrap">
                          {(() => {
                            const isUserReport = item.scanType === 'user_report';
                            const descKey = getDescriptionKey(item);
                            const banProfileId = ownerProfile?.id ?? item.reportedUser?.id ?? null;
                            const banDisplayName =
                              ownerProfile?.displayName ?? item.reportedUser?.displayName ?? displayName;
                            const isBanDisabled = !!(ownerProfile?.isDisabled ?? item.reportedUser?.isDisabled);
                            const isBanBanned = !!(ownerProfile?.isBanned ?? item.reportedUser?.isBanned);
                            return (
                              <>
                                {/* Red button: Remove (all) / Confirm (user_report) */}
                                {status !== 'removed' && (
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="text-xs h-7 px-2"
                                    onClick={() => {
                                      if (isUserReport) {
                                        setPendingConfirm({
                                          title: t('moderation.confirm-confirm.title'),
                                          description: t('moderation.confirm-confirm.description'),
                                          confirm: t('moderation.action-confirm'),
                                          confirmVariant: 'destructive',
                                          onConfirm: () => handleReview(item.id, 'rejected'),
                                        });
                                      } else {
                                        setPendingConfirm({
                                          title: t('moderation.confirm-remove.title'),
                                          description: t(`moderation.confirm-remove.description-${descKey}`),
                                          confirm: t('moderation.action-remove'),
                                          confirmVariant: 'destructive',
                                          onConfirm: () =>
                                            handleReview(item.id, 'rejected', t('moderation.violates-community-rules')),
                                        });
                                      }
                                    }}
                                    disabled={reviewLoading && reviewingId === item.id}
                                  >
                                    {isUserReport ? t('moderation.action-confirm') : t('moderation.action-remove')}
                                  </Button>
                                )}
                                {/* Green button: Allow (all) / Dismiss (user_report) / Restore (from removed tab) */}
                                {status !== 'approved' && item.resolution !== 'deleted' && (
                                  <Button
                                    size="sm"
                                    className="bg-green-500 hover:bg-green-600 text-white text-xs h-7 px-2"
                                    onClick={() => {
                                      if (status === 'removed') {
                                        setPendingConfirm({
                                          title: t('moderation.confirm-restore.title'),
                                          description: t('moderation.confirm-restore.description'),
                                          confirm: tc('restore'),
                                          onConfirm: () => handleReview(item.id, 'approved'),
                                        });
                                      } else if (isUserReport) {
                                        setPendingConfirm({
                                          title: t('moderation.confirm-dismiss.title'),
                                          description: t('moderation.confirm-dismiss.description'),
                                          confirm: t('moderation.action-dismiss'),
                                          onConfirm: () => handleReview(item.id, 'approved'),
                                        });
                                      } else {
                                        setPendingConfirm({
                                          title: t('moderation.confirm-allow.title'),
                                          description: t(`moderation.confirm-allow.description-${descKey}`),
                                          confirm: t('moderation.action-allow'),
                                          onConfirm: () => handleReview(item.id, 'approved'),
                                        });
                                      }
                                    }}
                                    disabled={reviewLoading && reviewingId === item.id}
                                  >
                                    {status === 'removed'
                                      ? tc('restore')
                                      : isUserReport
                                        ? t('moderation.action-dismiss')
                                        : t('moderation.action-allow')}
                                  </Button>
                                )}
                                {/* User action icon buttons: shown when a ban-able profile is available */}
                                {banProfileId && (
                                  <TooltipProvider delayDuration={300}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          size="sm"
                                          variant="destructive"
                                          className="h-7 w-7 p-0 disabled:opacity-40"
                                          disabled={isBanDisabled || updatingProfile}
                                          onClick={() => handleDisableUser(banProfileId, banDisplayName)}
                                        >
                                          <UserX className="w-3.5 h-3.5" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent side="top">
                                        {isBanDisabled
                                          ? t('moderation.user-flag.disabled')
                                          : t('moderation.action-disable-user')}
                                      </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          size="sm"
                                          variant="destructive"
                                          className="h-7 w-7 p-0 bg-red-700 hover:bg-red-800 text-white disabled:opacity-40"
                                          disabled={isBanBanned || updatingProfile}
                                          onClick={() => handleBanContentOwner(banProfileId, banDisplayName)}
                                        >
                                          <Ban className="w-3.5 h-3.5" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent side="top">
                                        {isBanBanned
                                          ? t('moderation.user-flag.banned')
                                          : t('moderation.action-ban-user')}
                                      </TooltipContent>
                                    </Tooltip>
                                    {banProfileId && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7 w-7 p-0 border-orange-200 text-orange-700 hover:bg-orange-50 disabled:opacity-40"
                                            disabled={
                                              !!(ownerProfile?.isNsfw || item.reportedUser?.isNsfw) || updatingProfile
                                            }
                                            onClick={() => handleNsfwUser(banProfileId, banDisplayName)}
                                          >
                                            <ShieldOff className="w-3.5 h-3.5" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                          {ownerProfile?.isNsfw || item.reportedUser?.isNsfw
                                            ? t('moderation.user-flag.nsfw')
                                            : t('moderation.action-nsfw-user')}
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                  </TooltipProvider>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="py-4 flex items-center justify-center">
            {isFetchingMore && <Loader2 className="w-5 h-5 animate-spin text-gray-400" />}
            {!hasNextPage && queue.length > 0 && (
              <p className="text-xs text-gray-400">{t('moderation.all-loaded', { count: queue.length })}</p>
            )}
          </div>
        </>
      )}

      {/* Content preview dialog */}
      <Dialog
        open={previewItem !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewItem(null);
            setRevealedImages(new Set());
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {previewItem?.communityPost
                ? t('moderation.preview.post')
                : previewItem?.communityComment
                  ? t('moderation.preview.comment')
                  : previewItem?.video
                    ? t('moderation.preview.video')
                    : previewItem?.imageComment
                      ? t('moderation.preview.artwork-comment')
                      : previewItem && flaggedStill(previewItem)
                        ? t('moderation.preview.image')
                        : previewItem && isDirectMessageItem(previewItem)
                          ? t('moderation.preview.direct-message')
                          : previewItem && isConnectionRequestMessageItem(previewItem)
                            ? t('moderation.preview.connection-request')
                            : previewItem && isRecommendationMessageItem(previewItem)
                              ? t('moderation.preview.recommendation')
                              : t('moderation.preview.comment')}
            </DialogTitle>
            <DialogDescription className="sr-only">{t('moderation.preview.description-sr-only')}</DialogDescription>
          </DialogHeader>

          {previewItem?.communityPost &&
            (() => {
              const post = previewItem.communityPost!;
              const postType = post.type?.toUpperCase() ?? 'TEXT';
              const name = authorDisplayName(post.author as any, t);
              const timeAgo = safeFormatDistance(post.createdAt);
              // A MEDIA (or CONTEST_MEDIA) post's carousel images reach socialImages
              // the same way an IMAGE post's do, so a moderator sees what they are judging.
              const images =
                postType === 'IMAGE' || postType === 'MEDIA' || postType === 'CONTEST_MEDIA'
                  ? (post.socialImages ?? [])
                  : (post.textImages ?? []);
              const postLabel = postRowLabel(post.title, post.content);

              return (
                <div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                    {post.author?.profile?.avatarUrl ? (
                      <img src={post.author.profile.avatarUrl} alt="" className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-gray-200" />
                    )}
                    <span className="font-medium text-gray-900">{name}</span>
                    <span>{timeAgo}</span>
                  </div>
                  {/* Titleless posts fall back to their body so the queue entry stays identifiable */}
                  {postLabel ? <h2 className="text-lg font-semibold text-gray-900 mb-3">{postLabel}</h2> : null}

                  {images.length > 0 && (
                    <div className="mb-3 space-y-2">
                      {images.map((img) => {
                        const url = img.optimizedUrl || img.thumbnailUrl;
                        if (!url) return null;
                        const isSensitive =
                          img.isNsfw ||
                          (NSFW_SCAN_TYPES.has(previewItem!.scanType) && previewItem!.resolution !== 'approved');
                        const isBlurred = isSensitive && !revealedImages.has(img.id);
                        return (
                          <div key={img.id} className="relative rounded-lg overflow-hidden">
                            <img
                              src={url}
                              alt=""
                              className={`w-full transition-[filter] duration-300 ${isBlurred ? 'blur-[20px]' : ''}`}
                            />
                            {isBlurred && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none">
                                <span className="flex items-center gap-1 text-white text-xs">
                                  <ShieldOff className="w-3.5 h-3.5" />
                                  {t('moderation.sensitive')}
                                </span>
                              </div>
                            )}
                            {isSensitive && (
                              <button
                                onClick={() => toggleImageReveal(img.id)}
                                className="absolute top-2 left-1/2 -translate-x-1/2 z-20 bg-white/90 hover:bg-white text-gray-900 text-[0.7rem] font-medium px-2.5 py-1 rounded-full whitespace-nowrap transition-colors"
                              >
                                {isBlurred ? t('moderation.sensitive') : t('moderation.hide')}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {post.content && (
                    <div
                      className="prose prose-sm max-w-none text-sm text-gray-600 mb-3"
                      dangerouslySetInnerHTML={{
                        __html: images.length > 0 ? post.content.replace(/<img[^>]*>/gi, '') : post.content,
                      }}
                    />
                  )}
                </div>
              );
            })()}

          {previewItem?.communityComment &&
            (() => {
              const comment = previewItem.communityComment!;
              const name = authorDisplayName(comment.author as any, t);
              const timeAgo = safeFormatDistance(comment.createdAt);
              // Collect image IDs to blur. For social_image scans the flagged image IS the
              // contentId. For community_comment scans blur every embedded social image.
              // Either way we use blurNsfwImages so both cases share one consistent UI.
              let processedHtml = comment.content;
              if (previewItem.resolution !== 'approved' && comment.content) {
                const nsfwIds: string[] = [];
                if (previewItem.contentType === 'social_image') {
                  nsfwIds.push(String(previewItem.contentId));
                } else {
                  for (const match of comment.content.matchAll(/data-social-image-id="([^"]+)"/g)) {
                    nsfwIds.push(match[1]);
                  }
                }
                if (nsfwIds.length > 0) processedHtml = blurNsfwImages(comment.content, nsfwIds);
              }

              return (
                <div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                    {comment.author?.profile?.avatarUrl ? (
                      <img
                        src={comment.author.profile.avatarUrl}
                        alt=""
                        className="w-6 h-6 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-gray-200" />
                    )}
                    <span className="font-medium text-gray-900">{name}</span>
                    <span>{timeAgo}</span>
                  </div>
                  {/* Omitted for a titleless parent post — this row carries no body to fall back on */}
                  {comment.post?.title?.trim() && (
                    <p className="text-xs text-gray-500 mb-2">
                      {t('moderation.in-reply-to')}{' '}
                      <span className="font-medium text-gray-700">{comment.post.title}</span>
                    </p>
                  )}
                  {processedHtml && (
                    <div
                      className="prose prose-sm max-w-none text-sm text-gray-600"
                      onClick={(e) => handleNsfwContainerClick(e)}
                      dangerouslySetInnerHTML={{ __html: processedHtml }}
                    />
                  )}
                </div>
              );
            })()}

          {previewItem?.imageComment &&
            (() => {
              const comment = previewItem.imageComment!;
              const owner = previewItem.contentOwner;
              const name = owner ? authorDisplayName(owner as any, t) : t('moderation.author-unknown');

              return (
                <div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                    {owner?.profile?.avatarUrl ? (
                      <img src={owner.profile.avatarUrl} alt="" className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-gray-200" />
                    )}
                    <span className="font-medium text-gray-900">{name}</span>
                    <span>{safeFormatDistance(comment.createdAt)}</span>
                  </div>
                  {/* Artwork comments are plain text, so no HTML is rendered here. */}
                  {comment.content && (
                    <p className="text-sm text-gray-600 whitespace-pre-wrap break-words">{comment.content}</p>
                  )}
                </div>
              );
            })()}

          {previewItem &&
            !!flaggedStill(previewItem) &&
            !previewItem?.communityPost &&
            !previewItem?.communityComment &&
            (() => {
              const img = flaggedStill(previewItem)!;
              const url = img.optimizedUrl || img.thumbnailUrl;
              if (!url) return null;
              const videoUrl = flaggedVideoUrl(previewItem);
              const isSensitive =
                img.isNsfw || (NSFW_SCAN_TYPES.has(previewItem.scanType) && previewItem.resolution !== 'approved');
              const isBlurred = isSensitive && !revealedImages.has(img.id);
              const owner = previewItem.contentOwner;
              const ownerName = owner ? authorDisplayName(owner as any, t) : null;

              return (
                <div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                    {owner?.profile?.avatarUrl ? (
                      <img src={owner.profile.avatarUrl} alt="" className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-gray-200" />
                    )}
                    {owner?.profile?.slug ? (
                      <a
                        href={`${__TOWN_SQUARE_URL__}/${owner.profile.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-gray-900 hover:underline"
                      >
                        {ownerName}
                      </a>
                    ) : (
                      <span className="font-medium text-gray-900">{ownerName}</span>
                    )}
                    <span>{safeFormatDistance(previewItem.createdAt)}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">{t('moderation.flagged-by-ai')}</p>
                  <div className="relative rounded-lg overflow-hidden">
                    {videoUrl ? (
                      <FlaggedVideo src={videoUrl} poster={url} isBlurred={isBlurred} />
                    ) : (
                      <img
                        src={url}
                        alt=""
                        className={`w-full transition-[filter] duration-300 ${isBlurred ? 'blur-[20px]' : ''}`}
                      />
                    )}
                    {isBlurred && (
                      // Takes the clicks rather than letting them through: a video with
                      // no `controls` has no play affordance in any browser we support,
                      // but the overlay is the last thing standing between a blurred
                      // clip and a stray click, and the reveal button stacks above it.
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <span className="flex items-center gap-1 text-white text-xs">
                          <ShieldOff className="w-3.5 h-3.5" />
                          {t('moderation.sensitive')}
                        </span>
                      </div>
                    )}
                    {isSensitive && (
                      <button
                        onClick={() => toggleImageReveal(img.id)}
                        className="absolute top-2 left-1/2 -translate-x-1/2 z-20 bg-white/90 hover:bg-white text-gray-900 text-[0.7rem] font-medium px-2.5 py-1 rounded-full whitespace-nowrap transition-colors"
                      >
                        {isBlurred ? t('moderation.sensitive') : t('moderation.hide')}
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}

          {previewItem &&
            isDirectMessageItem(previewItem) &&
            previewItem.directMessage &&
            (() => {
              const dm = previewItem.directMessage!;
              const sender = getAuthor(previewItem);
              const senderName = sender ? authorDisplayName(sender as any, t) : null;
              const senderProfile =
                sender && 'profile' in sender
                  ? (sender.profile as { avatarUrl?: string | null } | null)
                  : sender && 'avatarUrl' in sender
                    ? sender
                    : null;

              return (
                <div>
                  {senderName && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                      {senderProfile?.avatarUrl ? (
                        <img src={senderProfile.avatarUrl} alt="" className="w-6 h-6 rounded-full object-cover" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-gray-200" />
                      )}
                      <span className="font-medium text-gray-900">{senderName}</span>
                    </div>
                  )}
                  {dm.content ? (
                    <p className="text-sm text-gray-800 whitespace-pre-wrap rounded-lg bg-gray-50 p-3 border border-gray-100">
                      {dm.content}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">{t('moderation.preview.no-direct-message')}</p>
                  )}
                </div>
              );
            })()}

          {previewItem &&
            isConnectionRequestMessageItem(previewItem) &&
            (() => {
              const sender = previewItem.reportedUser;
              return (
                <div>
                  {sender && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                      {sender.avatarUrl ? (
                        <img src={sender.avatarUrl} alt="" className="w-6 h-6 rounded-full object-cover" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-gray-200" />
                      )}
                      <span className="font-medium text-gray-900">{sender.displayName}</span>
                    </div>
                  )}
                  {previewItem.connectionRequestMessage ? (
                    <p className="text-sm text-gray-800 whitespace-pre-wrap rounded-lg bg-gray-50 p-3 border border-gray-100">
                      {previewItem.connectionRequestMessage}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">{t('moderation.preview.no-message')}</p>
                  )}
                </div>
              );
            })()}

          {previewItem &&
            isRecommendationMessageItem(previewItem) &&
            (() => {
              const sender = previewItem.reportedUser;
              return (
                <div>
                  {sender && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                      {sender.avatarUrl ? (
                        <img src={sender.avatarUrl} alt="" className="w-6 h-6 rounded-full object-cover" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-gray-200" />
                      )}
                      <span className="font-medium text-gray-900">{authorDisplayName(sender as any, t)}</span>
                    </div>
                  )}
                  {previewItem.recommendationBody ? (
                    <p className="text-sm text-gray-800 whitespace-pre-wrap rounded-lg bg-gray-50 p-3 border border-gray-100">
                      {previewItem.recommendationBody}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">{t('moderation.preview.no-message')}</p>
                  )}
                </div>
              );
            })()}
        </DialogContent>
      </Dialog>

      <AlertDialog open={pendingConfirm !== null} onOpenChange={(open) => !open && setPendingConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingConfirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{pendingConfirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tc('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                pendingConfirm?.onConfirm();
                setPendingConfirm(null);
              }}
              className={
                pendingConfirm?.confirmVariant === 'destructive'
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : undefined
              }
            >
              {pendingConfirm?.confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
